import axios from "axios";
import keycloakInstance from "@/initKeycloak";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import type { SearchIntent } from "./types";
import { normaliseIntent } from "./normalise";

export * from "./types";
export { normaliseIntent } from "./normalise";

/**
 * Off until the /search-intent path is live on the environment, matching how
 * the onboarding concierge shipped.
 */
export const searchIntentEnabled =
  import.meta.env.VITE_CONCIERGE_ENABLED === "true";

const baseURL = import.meta.env.VITE_CONCIERGE_BASE_URL || "/api/concierge";

const client = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  /**
   * Far shorter than the plan client's 150s. This is a parsing job on a small
   * prompt with no catalog, so it should answer in a second or two — and a
   * search box that hangs for a minute is worse than one that gives up and
   * lets the user search by hand.
   */
  timeout: 20_000,
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30);
    } catch (error) {
      console.error("Token refresh error in search-intent interceptor:", error);
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

attachRetryOn401(client);

export async function requestSearchIntent(
  query: string,
  signal?: AbortSignal
): Promise<SearchIntent> {
  const { data } = await client.post<Partial<SearchIntent>>(
    "/search-intent",
    { query },
    { signal }
  );
  return normaliseIntent(data ?? {});
}
